import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";

type Tone = "info" | "success" | "warning" | "danger";
type Toast = {
  id: number;
  tone: Tone;
  title?: React.ReactNode;
  body?: React.ReactNode;
  duration?: number;
  action?: { label: string; onClick: () => void };
};

type ToastApi = {
  push: (t: Omit<Toast, "id">) => number;
  dismiss: (id: number) => void;
  info:    (msg: string, opts?: Partial<Toast>) => number;
  success: (msg: string, opts?: Partial<Toast>) => number;
  warning: (msg: string, opts?: Partial<Toast>) => number;
  danger:  (msg: string, opts?: Partial<Toast>) => number;
};

const ToastContext = createContext<ToastApi | null>(null);

const tones: Record<Tone, string> = {
  info:    "bg-[var(--c-info)]    text-white",
  success: "bg-[var(--c-success)] text-white",
  warning: "bg-[var(--c-warning)] text-white",
  danger:  "bg-[var(--c-danger)]  text-white",
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((t: Omit<Toast, "id">) => {
    const id = idRef.current++;
    const next: Toast = { duration: 4000, ...t, id };
    setToasts((cur) => [...cur, next]);
    return id;
  }, []);

  const api: ToastApi = {
    push, dismiss,
    info:    (msg, opts) => push({ tone: "info",    title: msg, ...opts }),
    success: (msg, opts) => push({ tone: "success", title: msg, ...opts }),
    warning: (msg, opts) => push({ tone: "warning", title: msg, ...opts }),
    danger:  (msg, opts) => push({ tone: "danger",  title: msg, ...opts }),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, dismiss }: { toast: Toast; dismiss: (id: number) => void }) {
  useEffect(() => {
    if (!toast.duration || toast.duration <= 0) return;
    const t = window.setTimeout(() => dismiss(toast.id), toast.duration);
    return () => window.clearTimeout(t);
  }, [toast.duration, toast.id, dismiss]);

  return (
    <div
      role={toast.tone === "danger" || toast.tone === "warning" ? "alert" : "status"}
      className={cx(
        "pointer-events-auto flex max-w-md gap-3 rounded-lg px-3 py-2.5 text-sm shadow-modal animate-slide-up",
        tones[toast.tone]
      )}
    >
      <div className="min-w-0 flex-1">
        {toast.title && <div className="font-semibold">{toast.title}</div>}
        {toast.body && <div className="mt-0.5 opacity-95">{toast.body}</div>}
      </div>
      {toast.action && (
        <button
          type="button"
          onClick={() => { toast.action!.onClick(); dismiss(toast.id); }}
          className="self-center rounded bg-white/15 px-2 py-1 text-xs font-medium hover:bg-white/25"
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => dismiss(toast.id)}
        className="self-start rounded p-1 text-white/80 hover:bg-white/15"
      >
        ✕
      </button>
    </div>
  );
}

function ToastViewport({ toasts, dismiss }: { toasts: Toast[]; dismiss: (id: number) => void }) {
  const host = typeof document !== "undefined"
    ? document.getElementById("toast-root")
    : null;
  if (!host) return null;
  return createPortal(
    <>{toasts.map((t) => <ToastItem key={t.id} toast={t} dismiss={dismiss} />)}</>,
    host
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe fallback: log + noop. Components never crash if provider missing.
    const noop = (msg: string) => {
      // eslint-disable-next-line no-console
      console.warn("[toast]", msg);
      return -1;
    };
    return {
      push:    () => -1,
      dismiss: () => undefined,
      info: noop, success: noop, warning: noop, danger: noop,
    };
  }
  return ctx;
}
